import React, { useState, useEffect, useRef } from 'react';
import { 
  FiBook, FiZap, FiCreditCard, FiFileText, FiUpload,
  FiClock, FiSearch, FiSettings, FiTrendingUp, FiCommand
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import styles from '../styles/commandPalette.module.css';

const CommandPalette = ({ isOpen, onClose, onCommand }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  const commands = [
    {
      id: 'quiz',
      name: 'Generate Quiz',
      description: 'Create interactive quiz questions',
      icon: FiBook,
      color: '#3b82f6',
      keywords: ['quiz', 'questions', 'test', 'practice'],
      action: () => onCommand('quiz')
    },
    {
      id: 'flashcards',
      name: 'Generate Flashcards',
      description: 'Create flashcards for quick review',
      icon: FiZap,
      color: '#f59e0b',
      keywords: ['flashcards', 'cards', 'study', 'review', 'memorize'],
      action: () => onCommand('flashcards')
    },
    {
      id: 'test',
      name: 'Create Test',
      description: 'Generate comprehensive assessment',
      icon: FiCreditCard,
      color: '#8b5cf6',
      keywords: ['test', 'exam', 'assessment', 'evaluation'],
      action: () => onCommand('test')
    },
    {
      id: 'notes',
      name: 'Study Notes',
      description: 'Generate organized study notes',
      icon: FiFileText,
      color: '#10b981',
      keywords: ['notes', 'summary', 'outline', 'study guide'],
      action: () => onCommand('notes')
    },
    {
      id: 'upload',
      name: 'Upload Document',
      description: 'Upload PDF, DOC, or text file',
      icon: FiUpload,
      color: '#ec4899',
      keywords: ['upload', 'document', 'file', 'pdf', 'import'],
      action: () => onCommand('upload')
    },
    {
      id: 'timer',
      name: 'Start Study Timer',
      description: 'Begin focused study session',
      icon: FiClock,
      color: '#14b8a6',
      keywords: ['timer', 'pomodoro', 'focus', 'session'],
      action: () => onCommand('timer')
    },
    {
      id: 'search',
      name: 'Search Documents',
      description: 'Find in your uploaded documents',
      icon: FiSearch,
      color: '#6366f1',
      keywords: ['search', 'find', 'look', 'query'],
      action: () => onCommand('search')
    },
    {
      id: 'progress',
      name: 'View Progress',
      description: 'See your study analytics',
      icon: FiTrendingUp,
      color: '#06b6d4',
      keywords: ['progress', 'stats', 'analytics', 'dashboard'],
      action: () => onCommand('progress')
    },
    {
      id: 'settings',
      name: 'Settings',
      description: 'Customize your experience',
      icon: FiSettings,
      color: '#64748b',
      keywords: ['settings', 'preferences', 'options', 'config'],
      action: () => onCommand('settings')
    }
  ];

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCommands(commands);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = commands.filter(cmd => 
        cmd.name.toLowerCase().includes(query) ||
        cmd.description.toLowerCase().includes(query) ||
        cmd.keywords.some(k => k.includes(query))
      );
      setFilteredCommands(filtered);
    }
    setSelectedIndex(0);
  }, [searchQuery]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredCommands.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredCommands.length - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action();
          onClose();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className={styles.overlay}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className={styles.palette}
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={styles.header}>
            <FiSearch size={20} className={styles.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Type a command or search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            <div className={styles.shortcut}>
              <FiCommand size={14} />
              <span>K</span>
            </div>
          </div>

          {/* Commands List */}
          <div className={styles.commandsList}>
            {filteredCommands.length > 0 ? (
              filteredCommands.map((command, index) => {
                const Icon = command.icon;
                return (
                  <motion.button
                    key={command.id}
                    className={`${styles.commandItem} ${
                      selectedIndex === index ? styles.selected : ''
                    }`}
                    onClick={() => {
                      command.action();
                      onClose();
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    whileHover={{ x: 4 }}
                  >
                    <div 
                      className={styles.commandIcon}
                      style={{ background: `${command.color}15` }}
                    >
                      <Icon size={20} style={{ color: command.color }} />
                    </div>
                    <div className={styles.commandContent}>
                      <div className={styles.commandName}>{command.name}</div>
                      <div className={styles.commandDescription}>
                        {command.description}
                      </div>
                    </div>
                    {selectedIndex === index && (
                      <motion.div
                        className={styles.enterHint}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                      >
                        ↵
                      </motion.div>
                    )}
                  </motion.button>
                );
              })
            ) : (
              <div className={styles.noResults}>
                <FiSearch size={32} />
                <p>No commands found</p>
                <span>Try a different search term</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <div className={styles.footerHint}>
              <span className={styles.key}>↑↓</span> Navigate
              <span className={styles.key}>↵</span> Select
              <span className={styles.key}>esc</span> Close
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CommandPalette;
